/**
 * The open tab of the country page, narrowed to the open CAMPAIGN GROUP:
 * the Board lists its containers with its master config as one small
 * control in the card header (or how to create it), and the group's
 * auto-send beside it (Phase 20), and above it the paste offer of a group
 * copied on another board; the Day timeline and Live show only its
 * containers; the tutorial is never narrowed.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { BoardCard } from '@/features/drop-generator/components/board/BoardCard';
import { ErrorsTab } from '@/features/drop-generator/components/errors-tab/ErrorsTab';
import { CreateMasterConfigButton } from '@/features/drop-generator/components/campaign/master/create/CreateMasterConfigButton';
import { GroupAutoSendControl } from '@/features/drop-generator/components/campaign/master/GroupAutoSendControl';
import { MasterConfigButton } from '@/features/drop-generator/components/campaign/master/MasterConfigButton';
import { PasteBar } from '@/features/drop-generator/components/group-copy/bar/PasteBar';
import { TimelineCard } from '@/features/drop-generator/components/timeline/TimelineCard';
import { HowItWorks } from '@/features/drop-generator/components/admin-guide/HowItWorks';
import type { EditorTabKey } from '@/features/drop-generator/components/editor/tabs/tab-chrome';
import type { DropGenPlanDetail } from '@/features/drop-generator/drop-generator.types';
import type { useDayPreview } from '@/features/drop-generator/useDropGenerator';
import { useDropGeneratorMutations } from '@/features/drop-generator/useDropGeneratorMutations';

import type { CountryTab } from '../CountryTabs';
import { GatewaysTab } from '../GatewaysTab';
import { LiveTab } from '../LiveTab';
import type { useCampaignScope } from './use-campaign-scope';

interface Props {
  tab: CountryTab;
  plan: DropGenPlanDetail;
  preview: ReturnType<typeof useDayPreview>;
  scope: ReturnType<typeof useCampaignScope>;
  nav: {
    at: string | null;
    chapter: number | null;
    /** ?part=admin — How it works › Admin guide (admins only). */
    adminPart?: boolean;
    setParams: (e: Record<string, string | null>) => void;
    onOpenMaster: (id: string | 'new', etab?: EditorTabKey) => void;
    onCopyMaster: (sourceId: string) => void;
    /** Opens a campaign group; absent where the page is locked to one. */
    onOpenGroup?: (groupKey: string) => void;
  };
}

export const CountryTabContent: FC<Props> = ({ tab, plan, preview, scope, nav }) => {
  const { resetSlot, skipSlot, disableSlot } = useDropGeneratorMutations();
  const { countryKey } = plan;
  const { current } = scope;
  const hasRouteTest = current?.master
    ? plan.containers.some((c) => c.kind === 'ROUTE_TEST' && c.masterId === current.master!.id)
    : false;
  if (tab === 'tutorial')
    return (
      <HowItWorks
        plan={plan}
        chapter={nav.chapter}
        onChapter={(n) => nav.setParams({ ch: n === null ? null : String(n) })}
        onBridge={(t) => nav.setParams(t)}
        adminPart={nav.adminPart ?? false}
        onPart={(admin) => nav.setParams({ part: admin ? 'admin' : null })}
      />
    );
  if (tab === 'live')
    return (
      <LiveTab
        countryKey={countryKey}
        at={nav.at}
        onSetAt={(next) => nav.setParams({ at: next })}
        preview={scope.preview}
        previewLoading={preview.isLoading}
        previewFailed={preview.isError}
        onRetryPreview={() => preview.refetch()}
        onEditContainer={(id) => nav.setParams({ container: id })}
        scopeIds={scope.ids}
      />
    );
  if (tab === 'gateways')
    return (
      <GatewaysTab
        countryKey={countryKey}
        at={nav.at}
        preview={scope.preview}
        previewLoading={preview.isLoading}
        previewFailed={preview.isError}
        onRetryPreview={() => preview.refetch()}
        scopeIds={scope.ids}
        scopeLabel={current?.label ?? null}
        onEditContainer={(id) => nav.setParams({ container: id })}
        onOpenMasterGateways={current?.master ? () => nav.onOpenMaster(current.master!.id, 'gateways') : undefined}
        onOpenErrors={() => nav.setParams({ tab: 'errors' })}
      />
    );
  if (tab === 'errors')
    return <ErrorsTab countryKey={countryKey} at={nav.at} scopeIds={scope.ids} scopeLabel={current?.label ?? null} onEditContainer={(id) => nav.setParams({ container: id })} />;
  if (tab === 'timeline')
    return (
      <TimelineCard
        plan={plan}
        preview={scope.preview}
        scopeLabel={current?.label ?? null}
        loading={preview.isFetching}
        failed={preview.isError}
        onRetry={() => preview.refetch()}
        onResetSlot={(slotId) => resetSlot.mutate({ countryKey, slotId })}
        onSkipSlot={(slotId) => skipSlot.mutate({ countryKey, slotId })}
        onDisableSlot={(containerId, slotId) => disableSlot.mutate({ countryKey, containerId, slotId })}
      />
    );
  const masterControl = current?.master ? (
    <>
      <GroupAutoSendControl plan={plan} masterId={current.master.id} onSettings={() => nav.onOpenMaster(current.master!.id, 'autosend')} />
      <MasterConfigButton master={current.master} plan={plan} group={current} onEdit={() => nav.onOpenMaster(current.master!.id)} />
    </>
  ) : current ? (
    <CreateMasterConfigButton
      groupLabel={current.label}
      standalone={plan.containers.filter((c) => scope.memberIds!.has(c.campaignWiserId) && c.masterId === null)}
      others={plan.masters ?? []}
      groupLabelOf={(key) => scope.ix.labelOf(key)}
      onCreate={() => nav.onOpenMaster('new')}
      onCopy={nav.onCopyMaster}
    />
  ) : null;
  return (
    <div className="flex flex-col gap-2">
      <PasteBar countryKey={countryKey} entries={scope.entries} current={current} onOpenGroup={nav.onOpenGroup} />
      <BoardCard
        plan={plan}
        preview={preview.data}
        previewLoading={preview.isLoading}
        previewFailed={preview.isError}
        onRetryPreview={() => preview.refetch()}
        scope={current ? { memberIds: scope.memberIds!, multi: current.members.length > 1 } : null}
        ix={scope.ix}
        addBlocked={scope.addBlocked}
        headerExtra={masterControl}
        onEdit={(id) => nav.setParams({ container: id })}
        onAdd={() => nav.setParams({ container: 'new' })}
        routeTestBlocked={current?.master ? (hasRouteTest ? 'This group already has a route test' : null) : 'Open a campaign group with a master config'}
        onAddRouteTest={current?.master && !hasRouteTest ? () => nav.setParams({ container: 'route-test' }) : undefined}
      />
    </div>
  );
};
